import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { useTranslation } from 'react-i18next';
import './AddPaymentMethodModal.css';

const API_URL = process.env.REACT_APP_FIREBASE_FUNCTIONS_URL ||
  "https://us-central1-sistema-bks.cloudfunctions.net";

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PK);

/**
 * Payment Form Component
 * Handles the Stripe Elements form and submission
 */
function PaymentForm({ user, organizationId, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const { t } = useTranslation('billing');
  const { t: tc } = useTranslation('common');

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Step 1: Create Setup Intent
      const idToken = await user.getIdToken();
      const setupResponse = await fetch(`${API_URL}/createSetupIntent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          uid: user.uid,
          email: user.email,
          ...(organizationId ? { organizationId } : {})
        }),
      });

      const { clientSecret, error: setupError } = await setupResponse.json();

      if (setupError || !clientSecret) {
        throw new Error(setupError || t('addPaymentModal.errorSetupIntent'));
      }

      // Step 2: Confirm Setup Intent with card details
      const cardElement = elements.getElement(CardElement);
      const { setupIntent, error: stripeError } = await stripe.confirmCardSetup(
        clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              email: user.email,
            },
          },
        }
      );

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      // Step 3: Attach payment method to customer
      const attachResponse = await fetch(`${API_URL}/attachPaymentMethod`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          uid: user.uid,
          paymentMethodId: setupIntent.payment_method,
          setAsDefault: true,
          ...(organizationId ? { organizationId } : {})
        }),
      });

      const attachData = await attachResponse.json();

      if (!attachData.success) {
        throw new Error(t('addPaymentModal.errorSaveMethod'));
      }

      // Success!
      onSuccess();
    } catch (err) {
      console.error('Error adding payment method:', err);
      setError(err.message || t('addPaymentModal.errorAddMethod'));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="payment-form">
      <div className="form-section">
        <label className="form-label">{t('addPaymentModal.cardInfo')}</label>
        <div className="card-element-container">
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#2d3748',
                  '::placeholder': {
                    color: '#a0aec0',
                  },
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                },
                invalid: {
                  color: '#e53e3e',
                },
              },
              hidePostalCode: true,
            }}
          />
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="modal-actions">
        <button
          type="button"
          onClick={onCancel}
          className="btn-cancel"
          disabled={processing}
        >
          {tc('cancel')}
        </button>
        <button
          type="submit"
          className="btn-submit"
          disabled={!stripe || processing}
        >
          {processing ? tc('processing') : t('addPaymentModal.addCard')}
        </button>
      </div>
    </form>
  );
}

/**
 * Modal Component
 * Wraps the payment form with Stripe Elements provider
 */
export default function AddPaymentMethodModal({ user, organizationId, isOpen, onClose, onSuccess }) {
  const { t } = useTranslation('billing');
  if (!isOpen) return null;

  const handleSuccess = () => {
    onSuccess();
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('addPaymentModal.title')}</h2>
          <button className="modal-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="modal-body">
          <p className="modal-description">
            {t('addPaymentModal.description')}
          </p>

          <Elements stripe={stripePromise}>
            <PaymentForm
              user={user}
              organizationId={organizationId}
              onSuccess={handleSuccess}
              onCancel={onClose}
            />
          </Elements>
        </div>

        <div className="modal-footer">
          <div className="security-badge">
            <span className="lock-icon">🔒</span>
            <span className="security-text">
              {t('addPaymentModal.securityText')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
